import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const SupplierInvoiceForm = ({ poId, accountingClientId, onSuccess, onCancel }) => {
  // Form state
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('net_30');
  const [lines, setLines] = useState([]);

  // Data state
  const [po, setPo] = useState(null);
  const [accountingClient, setAccountingClient] = useState(null);
  const [isVatRegistered, setIsVatRegistered] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Initialize today's date
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setInvoiceDate(today);
    setDueDate(today);
  }, []);

  // Fetch PO and AccountingClient on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setError('');
        const token = localStorage.getItem('authToken');

        // Fetch PO with items
        const poResponse = await axios.get(`/api/purchase-orders/${poId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const fetchedPo = poResponse.data.data || poResponse.data;
        setPo(fetchedPo);

        // Fetch PO items
        const itemsResponse = await axios.get(`/api/purchase-orders/${poId}/items`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const poItems = itemsResponse.data.data || itemsResponse.data || [];

        // Convert PO items to invoice lines
        const initialLines = poItems.map((item) => ({
          purchaseOrderItem: item._id,
          product: item.product,
          productName: item.productName,
          orderedQty: item.quantity || 0,
          receivedQty: item.receivedQuantity || 0,
          invoiceQty: item.receivedQuantity || 0, // Default to received qty
          unitPrice: item.cost || 0,
          poCost: item.cost || 0
        }));

        setLines(initialLines);

        // Fetch accounting client if provided
        if (accountingClientId) {
          const clientResponse = await axios.get(
            `/api/accounting-clients/${accountingClientId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const fetchedClient = clientResponse.data.data || clientResponse.data;
          setAccountingClient(fetchedClient);
          setIsVatRegistered(fetchedClient?.taxObligations?.vat === true);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(
          err.response?.data?.message || err.message || 'Failed to load purchase order'
        );
        setLoading(false);
      }
    };

    if (poId) {
      fetchData();
    }
  }, [poId, accountingClientId]);

  // Compute totals
  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, line) => {
      return sum + Number(line.invoiceQty || 0) * Number(line.unitPrice || 0);
    }, 0);

    const vatAmount = isVatRegistered ? subtotal * 0.12 : 0;
    const total = subtotal + vatAmount;

    return { subtotal, vatAmount, total };
  }, [lines, isVatRegistered]);

  // Handle line item changes
  const updateLine = (index, field, value) => {
    const updatedLines = [...lines];
    updatedLines[index][field] = field === 'invoiceQty' || field === 'unitPrice' ? Number(value) || 0 : value;
    setLines(updatedLines);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!invoiceNumber.trim()) {
      setError('Invoice Number is required');
      return;
    }
    if (!invoiceDate) {
      setError('Invoice Date is required');
      return;
    }
    if (!dueDate) {
      setError('Due Date is required');
      return;
    }

    try {
      setError('');
      setSubmitting(true);

      const token = localStorage.getItem('authToken');

      const payload = {
        poId,
        invoiceNumber: invoiceNumber.trim(),
        invoiceDate,
        dueDate,
        terms: paymentTerms,
        supplier: po?.supplier,
        supplierName: po?.supplierName,
        lines: lines.map((line) => ({
          purchaseOrderItem: line.purchaseOrderItem,
          product: line.product,
          productName: line.productName,
          quantity: line.invoiceQty,
          unitPrice: line.unitPrice,
          lineTotal: line.invoiceQty * line.unitPrice
        })),
        accountingClientId: accountingClientId || null
      };

      const response = await axios.post('/api/supplier-invoices', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const result = response.data;
      if (onSuccess) {
        onSuccess(result.matchResult || result.data);
      }
    } catch (err) {
      console.error('Error creating supplier invoice:', err);
      setError(
        err.response?.data?.message || err.message || 'Failed to create supplier invoice'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading purchase order...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
      {/* Error Message */}
      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Section 1: Invoice Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Invoice Number *
            </label>
            <input
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              placeholder="e.g. SINV-2026-00001"
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Invoice Date *
            </label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Due Date *
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Payment Terms
            </label>
            <select
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              disabled={submitting}
            >
              <option value="due_on_receipt">Due on Receipt</option>
              <option value="net_15">Net 15</option>
              <option value="net_30">Net 30</option>
              <option value="net_60">Net 60</option>
            </select>
          </div>
        </div>
      </div>

      {/* Section 2: Line Items Table */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h3>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">Product</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Ordered</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Received</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Invoice Qty</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Unit Price</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">Line Total</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => {
                const lineTotal = (line.invoiceQty || 0) * (line.unitPrice || 0);
                const isMismatch = line.invoiceQty !== line.receivedQty;

                return (
                  <tr
                    key={index}
                    className={`border-b border-gray-200 ${
                      isMismatch ? 'bg-amber-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {line.productName}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {line.orderedQty}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {line.receivedQty}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="number"
                        value={line.invoiceQty}
                        onChange={(e) => updateLine(index, 'invoiceQty', e.target.value)}
                        className="w-16 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 text-right"
                        min="0"
                        disabled={submitting}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="number"
                        value={line.unitPrice}
                        onChange={(e) => updateLine(index, 'unitPrice', e.target.value)}
                        className="w-24 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 text-right"
                        step="0.01"
                        min="0"
                        disabled={submitting}
                      />
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      ₱{lineTotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 3: Totals */}
      <div className="flex justify-end">
        <div className="w-full md:w-80 space-y-2 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex justify-between text-sm">
            <span className="text-gray-700">Subtotal:</span>
            <span className="font-medium">₱{totals.subtotal.toFixed(2)}</span>
          </div>

          {isVatRegistered && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-700">VAT (12%):</span>
              <span className="font-medium">₱{totals.vatAmount.toFixed(2)}</span>
            </div>
          )}

          <div className="border-t border-gray-300 pt-2 flex justify-between text-base font-semibold">
            <span>Total Amount:</span>
            <span className="text-teal-600">₱{totals.total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Section 4: Action Buttons */}
      <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50"
          disabled={submitting}
        >
          Cancel
        </button>

        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-teal-600 text-white rounded-md font-medium hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 flex items-center gap-2"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              Saving...
            </>
          ) : (
            'Save & Run 3-Way Match'
          )}
        </button>
      </div>
    </div>
  );
};

export default SupplierInvoiceForm;
